import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const APP_NAME  = 'Win/R';
const SUPPORT_EMAIL = 'support@imagisoft.co.uk';
const PRIVACY_URL   = '/privacy';
const TERMS_URL     = '/terms';

const faqs = [
  {
    id: 'cancel',
    q: 'How do I cancel my subscription?',
    a: `Subscriptions are managed through your device's app store — not through Win/R directly.\n\niOS: Open Settings → tap your Apple ID at the top → Subscriptions → Win/R → Cancel Subscription.\n\nAndroid: Open Google Play → tap your profile icon → Payments & subscriptions → Subscriptions → Win/R → Cancel.`,
  },
  {
    id: 'restore',
    q: 'I reinstalled the app and lost my subscription. How do I restore it?',
    a: `Open the app and go to the Subscription screen (Settings → Subscription). Tap "Restore Purchases". This will relink any active subscription tied to your Apple ID or Google account.`,
  },
  {
    id: 'refund',
    q: 'Can I get a refund?',
    a: `Refunds are handled by Apple or Google, not by Win/R directly.\n\niOS: Visit reportaproblem.apple.com and sign in with your Apple ID to request a refund.\n\nAndroid: Open Google Play → Order history → select the purchase → Request a refund.`,
  },
  {
    id: 'data',
    q: 'How do I delete my account and data?',
    a: `Email us at ${SUPPORT_EMAIL} with the subject line "Delete My Account" from the email address associated with your account. We will delete your account and personal data within 30 days, in accordance with our Privacy Policy.`,
  },
  {
    id: 'client-invite',
    q: 'How do I invite a client?',
    a: `Go to Settings → Invite a Client, enter your client's email address, and tap Send Invite. Your client will receive an email with a unique code to create their account and link to your profile automatically.`,
  },
  {
    id: 'stripe',
    q: 'How do I take payments from clients?',
    a: `Go to Settings → Payments → Connect Stripe. You will be guided through a brief Stripe onboarding process to connect your bank account. Once connected, you can issue invoices from the Invoices section and your clients can pay directly.`,
  },
  {
    id: 'password',
    q: 'I forgot my password. How do I reset it?',
    a: `On the login screen, tap "Forgot password?" and enter your email address. You will receive a reset link within a few minutes. Check your spam folder if it doesn't arrive.`,
  },
  {
    id: 'bug',
    q: 'I found a bug or something isn\'t working.',
    a: `Please email ${SUPPORT_EMAIL} with a brief description of what happened, which screen you were on, and your device type and OS version. Screenshots are always helpful. We aim to respond within one business day.`,
  },
];

export default function Support() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
          <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
        </button>
        <h1 style={{ flex: 1 }}>Support</h1>
      </div>

      <div className="page-content" style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* Hero card */}
        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--color-primary)' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>
            How can we help?
          </div>
          <div style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: 16 }}>
            Browse the frequently asked questions below, or reach out to our support team directly.
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--color-primary)', color: '#fff',
              padding: '10px 20px', borderRadius: 10,
              fontWeight: 600, fontSize: 14, textDecoration: 'none',
            }}
          >
            ✉ Email Support
          </a>
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-text-muted)' }}>
            {SUPPORT_EMAIL} · We respond within 1 business day
          </div>
        </div>

        {/* FAQ heading */}
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>
          Frequently Asked Questions
        </div>

        {/* FAQ accordion */}
        {faqs.map(item => (
          <div key={item.id} className="card" style={{ marginBottom: 10 }}>
            <button
              onClick={() => setActiveId(activeId === item.id ? null : item.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
            >
              <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--color-text)' }}>{item.q}</span>
              <span style={{ fontSize: 18, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: activeId === item.id ? 'rotate(180deg)' : 'none', flexShrink: 0, marginLeft: 12 }}>
                ›
              </span>
            </button>
            {activeId === item.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)', fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                {item.a}
              </div>
            )}
          </div>
        ))}

        {/* Legal links */}
        <div className="card" style={{ marginTop: 10, marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Legal</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <a href={PRIVACY_URL} style={{ fontSize: 14, color: 'var(--color-text)', textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Privacy Policy <span style={{ color: 'var(--color-text-muted)' }}>›</span>
            </a>
            <a href={TERMS_URL} style={{ fontSize: 14, color: 'var(--color-text)', textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
              Terms of Service <span style={{ color: 'var(--color-text-muted)' }}>›</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '8px 0 40px', fontSize: 13, color: 'var(--color-text-muted)' }}>
          Still need help? Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{SUPPORT_EMAIL}</a>
        </div>

      </div>
    </div>
  );
}
